import React, { useRef, useEffect } from "react";
import { AdminMenuList } from "../base/siteMaps";
import { useLocation } from "react-router-dom";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { useAuth } from "../base/context/authcontext";

const AdminSidebar = ({ isDrawerOpen, setIsDrawerOpen }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const sidebarRef = useRef(null);

  const { authData } = useAuth();
  const permissions = authData?.user_data?.role?.permissions || {};

  // Mapea rutas a la clave de módulo admin
  const pathToPermissionKey = {
    "/admin/properties": "admin_property",
    "/admin/users": "admin_user",
    "/admin/roles": "admin_roles",
    "/admin/ine_countries": "admin_roles",
  };

  // Fallback por nombre si aún no tienes las rutas /admin/... en AdminMenuList
  const nameToPermissionKey = (name = "") => {
    const n = name.toLowerCase();
    if (n.includes("propiedad")) return "admin_property";
    if (n.includes("usuario")) return "admin_user";
    if (n.includes("rol")) return "admin_roles";
    if (n.includes("ine")) return "admin_roles";
    return null;
  };

  const hasAdminAccess = (route) => {
    // 1) intenta por path
    const matchedPrefix = Object.keys(pathToPermissionKey).find((p) =>
      route.to?.startsWith?.(p)
    );
    if (matchedPrefix) {
      const moduleKey = pathToPermissionKey[matchedPrefix];
      return !!permissions?.[moduleKey]?.can_access_module;
    }
    // 2) fallback por nombre
    const moduleKeyByName = nameToPermissionKey(route.name);
    if (moduleKeyByName) {
      return !!permissions?.[moduleKeyByName]?.can_access_module;
    }
    // 3) si no mapea, muéstralo por defecto (o cambia a false si prefieres ocultar)
    return true;
  };

  // Cerrar drawer al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsDrawerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setIsDrawerOpen]);

  const visibleItems = (AdminMenuList.children || []).filter(hasAdminAccess);

  return (
    <>
      <div
        ref={sidebarRef}
        id="kt_app_sidebar"
        className={`kt_app_sidebar_style app-sidebar flex-column drawer drawer-start ${
          isDrawerOpen ? "drawer-on" : ""
        }`}
        data-kt-drawer="true"
        data-kt-drawer-name="app-sidebar"
        data-kt-drawer-activate="{default: true, lg: false}"
        data-kt-drawer-overlay="true"
        data-kt-drawer-width="100px"
        data-kt-drawer-direction="start"
        data-kt-drawer-toggle="#kt_app_sidebar_mobile_toggle"
      >
        {/* Logo */}
        <div
          className="app-sidebar-logo d-none d-lg-flex flex-center pt-8 mb-3"
          id="kt_app_sidebar_logo"
        >
          <a href="index.html">
            <img
              alt="Logo"
              src="/media/livensa/Studios-livensaliving.png"
              className="h-20px"
            />
          </a>
        </div>

        {/* Menú */}
        <div className="app-sidebar-menu d-flex flex-center align-items-start overflow-hidden flex-column-fluid">
          <div
            id="kt_app_sidebar_menu_wrapper"
            className="kt_app_sidebar_menu_wrapper_style app-sidebar-wrapper d-flex hover-scroll-overlay-y scroll-ms my-5"
            data-kt-scroll="true"
            data-kt-scroll-activate="true"
            data-kt-scroll-height="auto"
            data-kt-scroll-dependencies="#kt_app_sidebar_logo, #kt_app_sidebar_footer"
            data-kt-scroll-wrappers="#kt_app_sidebar_menu, #kt_app_sidebar"
            data-kt-scroll-offset="5px"
          >
            <div
              className="menu menu-column menu-rounded menu-active-bg menu-state-primary my-auto"
              id="#kt_app_sidebar_menu"
              data-kt-menu="true"
              data-kt-menu-expand="false"
            >
              {visibleItems.map((route, index) => {
                const isActive = currentPath.includes(route.to);
                return (
                  <Tippy key={index} content={route.name} placement="right">
                    <a href={route.to}>
                      <div
                        className={`menu-item py-2 ${
                          isActive ? "menu-item-active" : ""
                        }`}
                      >
                        <span className="menu-link menu-center">
                          <span
                            className={`menu-icon me-0 ${
                              isActive ? "menu-icon-active" : ""
                            }`}
                          >
                            <i
                              className={`fa-solid fa-${
                                route.icon
                              } fs-2x text-color-${
                                isActive ? "azul-livensa" : "blanco-livensa"
                              }`}
                            ></i>
                          </span>
                        </span>
                      </div>
                    </a>
                  </Tippy>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="app-sidebar-footer d-flex flex-center flex-column-auto pt-6 mb-7"
          id="kt_app_sidebar_footer"
        >
          <div className="mb-0">
            <button
              type="button"
              className="btn btm-sm btn-custom btn-icon"
              data-kt-menu-trigger="click"
              data-kt-menu-overflow="true"
              data-kt-menu-placement="top-start"
              data-bs-toggle="tooltip"
              data-bs-placement="right"
              data-bs-dismiss="click"
              aria-label="Quick actions"
              data-bs-original-title="Quick actions"
              data-kt-initialized="1"
            >
              <i className="fonticon-notification fs-1"></i>
            </button>

            <div
              className="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg-light-primary fw-semibold w-200px"
              data-kt-menu="true"
            >
              <div className="menu-item px-3">
                <div className="menu-content fs-6 text-gray-900 fw-bold px-3 py-4">
                  Quick Actions
                </div>
              </div>

              <div className="separator mb-3 opacity-75"></div>

              <div className="menu-item px-3">
                <a href="#" className="menu-link px-3">
                  New Ticket
                </a>
              </div>

              <div className="menu-item px-3">
                <a href="#" className="menu-link px-3">
                  New Customer
                </a>
              </div>

              <div
                className="menu-item px-3"
                data-kt-menu-trigger="hover"
                data-kt-menu-placement="right-start"
              >
                <a href="#" className="menu-link px-3">
                  <span className="menu-title">New Group</span>
                  <span className="menu-arrow"></span>
                </a>

                <div className="menu-sub menu-sub-dropdown w-175px py-4">
                  <div className="menu-item px-3">
                    <a href="#" className="menu-link px-3">
                      Admin Group
                    </a>
                  </div>

                  <div className="menu-item px-3">
                    <a href="#" className="menu-link px-3">
                      Staff Group
                    </a>
                  </div>

                  <div className="menu-item px-3">
                    <a href="#" className="menu-link px-3">
                      Member Group
                    </a>
                  </div>
                </div>
              </div>

              <div className="menu-item px-3">
                <a href="#" className="menu-link px-3">
                  New Contact
                </a>
              </div>

              <div className="separator mt-3 opacity-75"></div>

              <div className="menu-item px-3">
                <div className="menu-content px-3 py-3">
                  <a className="btn btn-primary btn-sm px-4" href="#">
                    Generate Reports
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* /Footer */}
      </div>
    </>
  );
};

export default AdminSidebar;
